/**
 * Отображение статуса заявки гостю (canon design-guest §17): человеко-
 * читаемая подпись + тон бейджа + порядок шагов трекинга.
 */
import type { RequestState } from '@hsc/types';

/** Тон бейджа статуса (маппится на CSS-класс rq-badge--{tone}). */
export type StatusTone = 'pending' | 'active' | 'done' | 'cancelled' | 'alert';

interface StatusMeta {
  label: string;
  tone: StatusTone;
  /** Подсказка под бейджем — что сейчас происходит. */
  hint: string;
}

const STATUS_META: Readonly<Record<string, StatusMeta>> = {
  created: { label: 'Создана', tone: 'pending', hint: 'Заявка принята, скоро возьмём в работу' },
  assigned: { label: 'Назначена', tone: 'active', hint: 'Сотрудник назначен на Вашу заявку' },
  in_progress: { label: 'В работе', tone: 'active', hint: 'Сотрудник занимается Вашей заявкой' },
  resolved: { label: 'Выполнена', tone: 'done', hint: 'Готово! Заявка выполнена' },
  cancelled: { label: 'Отменена', tone: 'cancelled', hint: 'Заявка отменена' },
  escalated: { label: 'На контроле', tone: 'alert', hint: 'Заявку взял на контроль менеджер' },
};

const FALLBACK: StatusMeta = { label: 'В обработке', tone: 'pending', hint: '' };

/** Метаданные статуса для UI (label/tone/hint), безопасно к неизвестным. */
export function statusMeta(state: string): StatusMeta {
  return STATUS_META[state] ?? { ...FALLBACK, label: state || FALLBACK.label };
}

/** Терминальные состояния — заявку нельзя отменять/комментировать дальше. */
export function isTerminal(state: string): boolean {
  return state === 'resolved' || state === 'cancelled';
}

/** Гость может отменить заявку только пока она не терминальна. */
export function canCancel(state: string): boolean {
  return !isTerminal(state);
}

/** Шаги трекера прогресса (canon §17). */
export const TRACK_STEPS: ReadonlyArray<{ state: RequestState; label: string }> = [
  { state: 'created', label: 'Создана' },
  { state: 'assigned', label: 'Назначена' },
  { state: 'in_progress', label: 'В работе' },
  { state: 'resolved', label: 'Выполнена' },
];

/** Индекс текущего шага в TRACK_STEPS (для подсветки прогресса). */
export function trackIndex(state: string): number {
  switch (state) {
    case 'created':
      return 0;
    case 'assigned':
    case 'escalated':
      return 1;
    case 'in_progress':
      return 2;
    case 'resolved':
      return 3;
    case 'cancelled':
      return -1;
    default:
      return 0;
  }
}
